// src/components/properties/PropertyPermissionsTab.js

import React, { useState, useEffect } from 'react';
import { Shield, Save, Check, X } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import '../../styles/PermissionsTab.css';

const FEATURES = [
    { key: 'time_tracking', label: 'Time Tracking' },
    { key: 'billing', label: 'Billing' },
    { key: 'domains', label: 'Domain Management' },
    { key: 'housekeeping', label: 'Housekeeping' },
    { key: 'social', label: 'Social Media' },
    { key: 'reports', label: 'Reports' },
    { key: 'support', label: 'Support' },
];

const ROLES = ['Owner', 'Manager', 'Accountant', 'Housekeeper'];

const PropertyPermissionsTab = ({ property }) => {
    const [permissions, setPermissions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        loadPermissions();
    }, [property.id]);

    const loadPermissions = async () => {
        try {
            setIsLoading(true);
            const data = await api.getPropertyPermissions(property.id);
            
            const permObj = {};
            ROLES.forEach(role => {
                permObj[role] = {};
                FEATURES.forEach(feature => {
                    const found = data.find(p => p.role === role && p.feature_key === feature.key);
                    permObj[role][feature.key] = found ? !!found.is_visible : (role === 'Manager');
                });
            });
            
            setPermissions(permObj);
            setHasChanges(false);
        } catch (error) {
            console.error('Failed to load permissions:', error);
            toast.error('Failed to load permissions.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = (role, featureKey) => {
        setPermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [featureKey]: !(prev[role]?.[featureKey])
            }
        }));
        setHasChanges(true);
    };

    const handleSelectAll = (role) => {
        setPermissions(prev => ({
            ...prev,
            [role]: FEATURES.reduce((acc, feature) => {
                acc[feature.key] = true;
                return acc;
            }, {})
        }));
        setHasChanges(true);
    };

    const handleDeselectAll = (role) => {
        setPermissions(prev => ({
            ...prev,
            [role]: FEATURES.reduce((acc, feature) => {
                acc[feature.key] = false;
                return acc;
            }, {})
        }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            
            const permissionsArray = [];
            ROLES.forEach(role => {
                // Skip Owner - Owner permissions are managed by SuperAdmin only
                if (role === 'Owner') return;
                
                FEATURES.forEach(feature => {
                    permissionsArray.push({
                        role,
                        feature_key: feature.key,
                        is_visible: permissions[role][feature.key]
                    });
                });
            });
            
            await api.updatePropertyPermissions(property.id, permissionsArray);
            toast.success('Permissions updated successfully.');
            
            // Reload data from server to ensure consistency
            await loadPermissions();
        } catch (error) {
            console.error('Failed to save permissions:', error);
            toast.error('Failed to save permissions.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="loading-state">Loading permissions...</div>;
    }

    return (
        <div className="permissions-tab">
            <div className="tab-content-header">
                <h3 className="tab-content-title">
                    <Shield size={20} style={{ marginRight: '8px' }} />
                    Role Permissions
                </h3>
                {hasChanges && (
                    <button 
                        className="action-button-small" 
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        <Save size={16} />
                        <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                )}
            </div>
            
            <p className="permissions-description">
                Control which features are visible to each role for this property.
                <br />
                <strong>Note:</strong> SuperAdmin always sees everything. Owner and Manager permissions are managed here.
            </p>

            {/* Desktop: Table View */}
            <div className="permissions-desktop">
                <div className="permissions-table-wrapper">
                    <table className="data-table permissions-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Feature</th>
                                {ROLES.map(role => (
                                    <th key={role} style={{ textAlign: 'center' }}>
                                        <div className="role-header">
                                            <span>{role}</span>
                                            {role !== 'Owner' && (
                                                <div className="role-actions">
                                                    <button 
                                                        className="icon-button-small"
                                                        onClick={() => handleSelectAll(role)}
                                                        title="Select All"
                                                    >
                                                        <Check size={12} />
                                                        <span>All</span>
                                                    </button>
                                                    <button 
                                                        className="icon-button-small"
                                                        onClick={() => handleDeselectAll(role)}
                                                        title="Deselect All"
                                                    >
                                                        <X size={12} />
                                                        <span>None</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {FEATURES.map(feature => (
                                <tr key={feature.key}>
                                    <td style={{ fontWeight: 500 }}>{feature.label}</td>
                                    {ROLES.map(role => (
                                        <td key={role} style={{ textAlign: 'center' }}>
                                            <label className="toggle-switch" style={{ margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={permissions[role]?.[feature.key] || false}
                                                    onChange={() => handleToggle(role, feature.key)}
                                                    disabled={role === 'Owner'}
                                                />
                                                <span className="slider"></span>
                                            </label>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile: Card View */}
            <div className="permissions-mobile">
                {ROLES.map(role => (
                    <div key={role} className="permission-role-card">
                        <div className="role-card-header">
                            <span className="role-card-title">{role}</span>
                            {role !== 'Owner' && (
                                <div className="role-card-actions">
                                    <button 
                                        className="icon-button-small"
                                        onClick={() => handleSelectAll(role)}
                                        title="Select All"
                                    >
                                        <Check size={14} />
                                        <span>All</span>
                                    </button>
                                    <button 
                                        className="icon-button-small"
                                        onClick={() => handleDeselectAll(role)}
                                        title="Deselect All"
                                    >
                                        <X size={14} />
                                        <span>None</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="role-card-features">
                            {FEATURES.map(feature => (
                                <div key={feature.key} className="feature-toggle-row">
                                    <span className="feature-label">{feature.label}</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={permissions[role]?.[feature.key] || false}
                                            onChange={() => handleToggle(role, feature.key)}
                                            disabled={role === 'Owner'}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PropertyPermissionsTab;
